/* The route editor, inside the merchandiser detail page's Routes tab.
   Normative rules: docs/design/spec/. There is no mockup for this screen — `merch-detail.html`
   covers only the read-only Routes tab and its "Open route editor" button.

   Stops are THREE ADDITIVE LAYERS (`route/routeModel.js`), and the editor shows all three rather
   than flattening them: a weekly base, extras on the Nth occurrence of a weekday in a month, and
   one-off additions on a single date. What a merchandiser visits on a date is
   base ++ weekAdd(nth) ++ dateAdd, deduped by point id. Saving re-materialises the layers into
   self-contained routes and reconciles them against the loaded snapshot, because the mobile app
   resolves one route per date and cannot filter stops by week. */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Flex, Tag, Typography } from "antd";
import { CalendarOutlined, SaveOutlined } from "@ant-design/icons";

import Alert from "/src/util/alert";

import { MERCHANDISER_MAIN_PAGE, ROUTE_CALENDAR } from "../../config/lang";
import { call } from "../../util/action";
import { getStore } from "../../util/redux/store";
import { translate } from "../../util/util";

import PointSearchPanel from "./route/PointSearchPanel";
import RouteCalendar from "./route/RouteCalendar";
import ScopeMenu from "./route/ScopeMenu";
import {
  addPoint,
  addPointBiweekly,
  desiredRoutesFromLayers,
  isBaseKey,
  isWeekKey,
  layersFromRoutes,
  movePoint,
  ordersForRoute,
  removePoint,
  routeSpecForKey,
} from "./route/routeModel";
import { addMonths, firstOfMonth } from "./route/dateUtils";

import "./route/style.scss";

const callP = body => new Promise(resolve => call(body, resolve));
const enWeekday = wd => new Intl.DateTimeFormat("en", { weekday: "long" }).format(new Date(2024, 0, 1 + wd));
const sameIds = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

const titleForKey = key => {
  if (isBaseKey(key)) return enWeekday(Number(key.slice(1)));
  if (isWeekKey(key)) {
    const [wd, nth] = key
      .slice(1)
      .split("_")
      .map(Number);
    return `${nth} ${enWeekday(wd)}`;
  }
  return key.slice(1);
};

// Diff the layers against the routes as loaded → routes to (re)create and ids to delete.
const computeReconcile = (layers, loaded, extraDeletedIds) => {
  const desired = desiredRoutesFromLayers(layers);
  const desiredKeys = new Set(desired.map(r => r.key));
  const toDelete = new Set(extraDeletedIds);
  const toCreate = [];
  desired.forEach(({ key, stops }) => {
    const want = stops.map(s => s.point.id);
    const prev = loaded[key];
    if (prev && sameIds(want, prev.pointIds)) return; // unchanged
    if (prev && prev.id) toDelete.add(prev.id); // replace: recreate with new stops/order
    toCreate.push({ key, stops });
  });
  Object.keys(loaded).forEach(key => {
    if (!desiredKeys.has(key) && loaded[key].id) toDelete.add(loaded[key].id);
  });
  return { toCreate, toDelete: [...toDelete] };
};

export default function Route({ user }) {
  const me = getStore().getState().me;
  const lang = me?.language || "en";
  const t = key => translate(ROUTE_CALENDAR, key, lang);

  const [layers, setLayers] = useState({ base: {}, week: {}, date: {} });
  const [loaded, setLoaded] = useState({});
  const [extraDeletedIds, setExtraDeletedIds] = useState([]);
  /* Days inside a recorded absence refuse new stops (PICSELL-1581 #14). One fetch per
     merchandiser; the presence tab pages the same endpoint, here 100 covers a person's records. */
  const [absences, setAbsences] = useState([]);
  const [monthDate, setMonthDate] = useState(() => firstOfMonth(new Date()));
  const [pendingDrop, setPendingDrop] = useState(null);
  /* The point selected in the panel, waiting for a day. This is what makes the editor usable
     without a mouse — see PointSearchPanel. */
  const [armed, setArmed] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!user?.id) return;
    call({ method: "GET", url: "routes?user=" + user.id }, resp => {
      const list = Array.isArray(resp) ? resp : resp?.results || [];
      /* ⚠ Load-bearing, not belt-and-braces — the same client-side filter /tracking carries.
         The endpoint is company-wide for a manager now (PICSELL-1595), so if `?user=` is ever
         dropped again (the filterset_class fix has already been lost to a deploy gap once),
         this line is what keeps the editor from merging EVERY merchandiser's routes into one
         calendar — and its save reconcile from then DELETING them. */
      const mine = list.filter(route => (route.user?.id ?? route.user) === user.id);
      const { layers: l, loaded: ld, extraDeletedIds: ex } = layersFromRoutes(mine);
      setLayers(l);
      setLoaded(ld);
      setExtraDeletedIds(ex);
    });
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const userId = user?.id;
  useEffect(() => {
    if (!userId) return;
    call({ method: "GET", url: `absences?merchandiser=${userId}&page_size=100` }, resp => {
      setAbsences(Array.isArray(resp) ? resp : resp?.results || []);
    });
  }, [userId]);

  const { toCreate, toDelete } = useMemo(() => computeReconcile(layers, loaded, extraDeletedIds), [layers, loaded, extraDeletedIds]);
  const hasChanges = toCreate.length > 0 || toDelete.length > 0;

  const addWithScope = (date, point, scope) => setLayers(l => (scope === "biweekly" ? addPointBiweekly(l, date, point) : addPoint(l, scope, date, point)));
  const removeStop = (layerKey, pointId) => setLayers(l => removePoint(l, layerKey, pointId));
  const moveStop = (layerKey, pointId, dir) => setLayers(l => movePoint(l, layerKey, pointId, dir));

  const save = async () => {
    if (!hasChanges || saving) return;
    setSaving(true);
    try {
      // Create first, then delete — a failed create leaves old routes intact (recoverable
      // duplicate, consolidated on next load) instead of losing data.
      for (const { key, stops } of toCreate) {
        const spec = routeSpecForKey(key, { userId: user.id, createdBy: me?.id ?? null, title: titleForKey(key) });
        const created = await callP({ method: "POST", url: "routes/", post: spec });
        if (!created || !created.id) throw new Error("route create failed: " + JSON.stringify(created));
        const orders = ordersForRoute(created.id, stops);
        if (orders.length) await callP({ method: "POST", url: "order/", post: orders });
      }
      for (const id of toDelete) {
        await callP({ method: "DELETE", url: `routes/${id}/` }); // orders cascade-delete
      }
      Alert.success(translate(MERCHANDISER_MAIN_PAGE, "data_successfully_saved", lang), { position: "bottom-right" });
      load();
    } catch (e) {
      Alert.warning(t("save_failed"), { position: "bottom-right" });
    } finally {
      setSaving(false);
    }
  };

  const stopCount = useMemo(() => {
    let n = 0;
    Object.values(layers.base).forEach(a => (n += a.length));
    Object.values(layers.week).forEach(byNth => Object.values(byNth).forEach(a => (n += a.length)));
    Object.values(layers.date).forEach(a => (n += a.length));
    return n;
  }, [layers]);

  return (
    <Flex vertical gap="middle" className="rc">
      <Flex className="rc-bar" align="center" gap="small" wrap>
        <CalendarOutlined className="rc-bar__icon" />
        <Typography.Text strong>{t("heading")}</Typography.Text>
        <Tag variant="filled" className="rc-bar__count">
          {t("stops_label")} {stopCount}
        </Tag>
        <span className="rc-spacer" />
        {/* Only ever shown while there is something to save, so it never states a falsehood. */}
        {hasChanges ? (
          <Typography.Text type="secondary" className="rc-bar__dirty" aria-live="polite">
            {t("unsaved_changes")}
          </Typography.Text>
        ) : null}
        <Button type="primary" icon={<SaveOutlined />} loading={saving} disabled={!hasChanges} onClick={save}>
          {translate(MERCHANDISER_MAIN_PAGE, "save_btn", lang)}
        </Button>
      </Flex>

      <div className="rc-layout">
        <PointSearchPanel lang={lang} armed={armed} onArm={setArmed} />
        <RouteCalendar
          monthDate={monthDate}
          layers={layers}
          lang={lang}
          armed={armed}
          absences={absences}
          onPrev={() => setMonthDate(m => addMonths(m, -1))}
          onNext={() => setMonthDate(m => addMonths(m, 1))}
          onDrop={(date, point) => setPendingDrop({ date, point })}
          onPlace={date => armed && setPendingDrop({ date, point: armed })}
          onRemove={removeStop}
          onMove={moveStop}
        />
      </div>

      <Typography.Text type="secondary" className="rc-legend">
        {t("legend")}
      </Typography.Text>

      {pendingDrop ? (
        <ScopeMenu
          date={pendingDrop.date}
          point={pendingDrop.point}
          lang={lang}
          onChoose={scope => {
            addWithScope(pendingDrop.date, pendingDrop.point, scope);
            setPendingDrop(null);
            /* The point has been placed; keeping it armed would litter every cell with "Add here"
               after the user is done. */
            setArmed(null);
          }}
          onCancel={() => setPendingDrop(null)}
        />
      ) : null}
    </Flex>
  );
}
